import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';
import { resolveTier } from '../../lib/tiers.js';

// Knowledge Base — admin upload UI for the RAG pipeline (Task #104).
//
// Vendor admins upload PDFs (equipment manuals, troubleshooting guides, SOPs)
// or paste plain text. Each document is then extracted, chunked, embedded, and
// stored so the customer-side AI triage (#100/#101) and responder-side AI
// assistant (#103) can retrieve relevant chunks for grounded answers.
//
// Backend lives in functions/src/ai-docs.js:
//   ingestDocument({ docId, displayName, sourceText? }) → kicks the pipeline
//   deleteDocument({ docId })                           → removes PDF + chunks
//   searchDocs({ query, limit? })                       → returns matching chunks
//
// Tier caps (mirrored from ai-docs.js MANUAL_LIMITS_BY_TIER):
//   pilot       = Infinity
//   free        = 0  (AI not available on free)
//   basic       = 3
//   enterprise  = Infinity
//
// The test-search panel at the bottom lets admins verify retrieval works on
// real queries before any AI flow consumes the index.

const MANUAL_LIMITS_BY_TIER = {
  pilot: Infinity,
  free: 0,
  basic: 3,
  enterprise: Infinity,
};

const MAX_PDF_BYTES = 10 * 1024 * 1024;     // matches storage.rules
const MAX_PASTED_CHARS = 200000;            // ~50 pages of text

export default function KnowledgeBase() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [docs, setDocs] = useState(null);
  const [planTier, setPlanTier] = useState('pilot');
  const [error, setError] = useState(null);

  // Subscribe to docs list + read vendor plan tier once.
  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      collection(db, 'vendors', vendorId, 'docs'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const at = a.uploadedAt?.toMillis ? a.uploadedAt.toMillis() : 0;
          const bt = b.uploadedAt?.toMillis ? b.uploadedAt.toMillis() : 0;
          return bt - at;
        });
        setDocs(rows);
      },
      (err) => setError(err.message),
    );
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'vendors', vendorId));
        if (snap.exists()) setPlanTier(snap.data().planTier || 'pilot');
      } catch {/* tier just stays 'pilot' */}
    })();
    return () => unsub();
  }, [vendorId]);

  const tier = useMemo(() => resolveTier(planTier), [planTier]);
  const cap = MANUAL_LIMITS_BY_TIER[planTier] ?? 0;
  const activeCount = (docs || []).filter((d) => d.status !== 'deleted').length;
  const atCap = cap !== Infinity && activeCount >= cap;
  const aiBlockedOnTier = cap === 0;

  async function handleDelete(docRow) {
    if (!confirm(`Delete "${docRow.displayName}"? This removes the indexed chunks and any future AI answers will lose this context.`)) return;
    setError(null);
    try {
      const fn = httpsCallable(functions, 'deleteDocument');
      await fn({ docId: docRow.docId || docRow.id });
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Knowledge Base</h2>
        <TierBadge tier={tier} cap={cap} count={activeCount} />
      </div>

      <p className="settings-help" style={{ marginTop: 8 }}>
        Upload equipment manuals, troubleshooting guides, and standard procedures.
        Once indexed, the AI assistant can quote them when answering customers and
        helping technicians on-site. PDFs only (max 10 MB) or paste text directly.
      </p>

      {error && <div className="error-banner" style={{ marginTop: 8 }}>{error}</div>}

      {aiBlockedOnTier && (
        <div className="placeholder" style={{ marginTop: 12, borderLeft: '3px solid #f59e0b' }}>
          AI knowledge base isn't available on the <strong>{tier.name}</strong> plan.
          Upgrade to <strong>Basic</strong> (3 documents) or <strong>Enterprise</strong> (unlimited)
          to start indexing manuals.
        </div>
      )}

      {!aiBlockedOnTier && (
        <AddDocumentPanel
          vendorId={vendorId}
          atCap={atCap}
          capLabel={cap === Infinity ? 'unlimited' : String(cap)}
        />
      )}

      <DocumentsList
        docs={docs}
        aiBlockedOnTier={aiBlockedOnTier}
        onDelete={handleDelete}
      />

      {!aiBlockedOnTier && (
        <TestSearchPanel
          hasReadyDocs={(docs || []).some((d) => d.status === 'ready')}
        />
      )}
    </>
  );
}

// ============================================================================
// TierBadge — quick "2 of 3 indexed · Basic plan" chip in the header
// ============================================================================

function TierBadge({ tier, cap, count }) {
  const capLabel = cap === Infinity ? 'unlimited' : String(cap);
  const danger = cap !== Infinity && count >= cap;
  return (
    <span style={{
      fontSize: 12, fontWeight: 600,
      padding: '4px 10px', borderRadius: 999,
      background: danger ? '#fee2e2' : '#eef2ff',
      color: danger ? '#991b1b' : '#3730a3',
      whiteSpace: 'nowrap',
    }}>
      {count} of {capLabel} indexed · {tier.name}
    </span>
  );
}

// ============================================================================
// AddDocumentPanel — Upload PDF / Paste text tabs
// ============================================================================

function AddDocumentPanel({ vendorId, atCap, capLabel }) {
  const [mode, setMode] = useState('pdf');  // 'pdf' | 'text'

  return (
    <div style={{
      marginTop: 16, padding: 12,
      background: '#f8fafc', borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>+ Add a document</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <TabButton active={mode === 'pdf'} onClick={() => setMode('pdf')}>Upload PDF</TabButton>
          <TabButton active={mode === 'text'} onClick={() => setMode('text')}>Paste text</TabButton>
        </div>
      </div>

      {atCap && (
        <div className="placeholder" style={{ marginBottom: 12, borderLeft: '3px solid #ef4444' }}>
          You've hit your plan's cap of {capLabel} indexed documents. Delete one
          below or upgrade to Enterprise to add more.
        </div>
      )}

      {mode === 'pdf' ? (
        <UploadPdfForm vendorId={vendorId} disabled={atCap} />
      ) : (
        <PasteTextForm vendorId={vendorId} disabled={atCap} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 12, fontWeight: 600,
        padding: '4px 10px', borderRadius: 6,
        border: '1px solid ' + (active ? 'transparent' : 'var(--border)'),
        background: active ? 'var(--accent, #4f46e5)' : 'white',
        color: active ? 'white' : 'var(--text, #111)',
        cursor: 'pointer',
      }}>
      {children}
    </button>
  );
}

// ============================================================================
// UploadPdfForm
// ============================================================================

function UploadPdfForm({ vendorId, disabled }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [error, setError] = useState(null);

  function onFilePicked(f) {
    setError(null);
    if (!f) { setFile(null); return; }
    if (f.size > MAX_PDF_BYTES) {
      setError(`PDF is ${(f.size / 1024 / 1024).toFixed(1)} MB; max is 10 MB. Try splitting it or pasting key sections as text.`);
      return;
    }
    if (f.type && f.type !== 'application/pdf') {
      setError('Only PDF files are supported.');
      return;
    }
    setFile(f);
    // Default the display name from the filename (strip extension).
    if (!name) {
      const stripped = f.name.replace(/\.pdf$/i, '');
      setName(stripped.slice(0, 200));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file || !name.trim() || disabled) return;
    setBusy(true); setError(null);
    try {
      const docId = newDocId();
      setStage('Uploading PDF...');
      const path = `vendors/${vendorId}/docs/${docId}.pdf`;
      await uploadBytes(storageRef(storage, path), file, {
        contentType: 'application/pdf',
      });

      setStage('Indexing (extract → chunk → embed)...');
      const fn = httpsCallable(functions, 'ingestDocument');
      await fn({ docId, displayName: name.trim() });

      // Success — clear the form so admin can add another.
      setFile(null);
      setName('');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
      setStage('');
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        type="text"
        placeholder='Display name (e.g. "Carrier 30HXC Service Manual")'
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={disabled || busy}
        style={inputStyle}
        maxLength={200}
      />
      <input
        type="file"
        accept="application/pdf,.pdf"
        onChange={(e) => onFilePicked(e.target.files?.[0] || null)}
        disabled={disabled || busy}
        style={{ ...inputStyle, padding: 8 }}
      />
      {file && (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          {file.name} — {(file.size / 1024).toFixed(0)} KB
        </div>
      )}
      {stage && <div style={{ fontSize: 12, color: '#3730a3' }}>{stage}</div>}
      {error && <div className="error-banner">{error}</div>}
      <button
        type="submit"
        disabled={disabled || busy || !file || !name.trim()}
        className="btn-primary"
        style={{ alignSelf: 'flex-start' }}>
        {busy ? 'Working...' : '+ Index this PDF'}
      </button>
    </form>
  );
}

// ============================================================================
// PasteTextForm
// ============================================================================

function PasteTextForm({ vendorId, disabled }) {
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !text.trim() || disabled) return;
    if (text.length > MAX_PASTED_CHARS) {
      setError(`Text is ${text.length.toLocaleString()} characters; max is ${MAX_PASTED_CHARS.toLocaleString()}. Trim it or split into two documents.`);
      return;
    }
    setBusy(true); setError(null);
    try {
      const docId = newDocId();
      const fn = httpsCallable(functions, 'ingestDocument');
      await fn({ docId, displayName: name.trim(), sourceText: text });
      setName(''); setText('');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        type="text"
        placeholder='Display name (e.g. "Cool-room callout SOP")'
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={disabled || busy}
        style={inputStyle}
        maxLength={200}
      />
      <textarea
        placeholder="Paste the SOP, procedure, or article text here..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={disabled || busy}
        rows={10}
        style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
      />
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
        {text.length.toLocaleString()} / {MAX_PASTED_CHARS.toLocaleString()} characters
      </div>
      {error && <div className="error-banner">{error}</div>}
      <button
        type="submit"
        disabled={disabled || busy || !name.trim() || !text.trim()}
        className="btn-primary"
        style={{ alignSelf: 'flex-start' }}>
        {busy ? 'Indexing...' : '+ Index this text'}
      </button>
    </form>
  );
}

// ============================================================================
// DocumentsList — table of indexed docs with status + delete
// ============================================================================

function DocumentsList({ docs, aiBlockedOnTier, onDelete }) {
  if (docs === null) {
    return <div className="placeholder" style={{ marginTop: 20 }}>Loading...</div>;
  }
  if (docs.length === 0) {
    return (
      <div className="placeholder" style={{ marginTop: 20 }}>
        {aiBlockedOnTier
          ? 'No documents yet. Upgrade your plan to start indexing.'
          : 'No documents indexed yet. Upload your first manual above.'}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {docs.map((d) => (
        <DocRow key={d.id} doc={d} onDelete={() => onDelete(d)} />
      ))}
    </div>
  );
}

function DocRow({ doc: d, onDelete }) {
  const isBusy = ['extracting', 'chunking', 'embedding'].includes(d.status);

  return (
    <div className="asset-row" style={{ alignItems: 'center' }}>
      <div className="asset-row-meta">
        <div className="asset-row-name">
          {d.displayName || d.docId}
          <StatusBadge status={d.status} hasEmbeddings={d.hasEmbeddings} />
        </div>
        <div className="asset-row-sub">
          {d.kind === 'pasted' ? 'Pasted text' : 'PDF'}
          {d.pageCount != null && <span> · {d.pageCount} page{d.pageCount === 1 ? '' : 's'}</span>}
          {d.chunkCount > 0 && <span> · {d.chunkCount} chunk{d.chunkCount === 1 ? '' : 's'}</span>}
          {d.errorMessage && <span style={{ color: '#991b1b' }}> · {d.errorMessage}</span>}
        </div>
      </div>
      <div className="asset-row-actions">
        <button
          onClick={onDelete}
          disabled={isBusy}
          className="link-danger"
          style={{ background: 'none', border: 'none', cursor: isBusy ? 'not-allowed' : 'pointer', opacity: isBusy ? 0.4 : 1 }}>
          Delete
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status, hasEmbeddings }) {
  const colors = {
    extracting:  { bg: '#dbeafe', fg: '#1e40af', label: 'Extracting' },
    chunking:    { bg: '#dbeafe', fg: '#1e40af', label: 'Chunking' },
    embedding:   { bg: '#dbeafe', fg: '#1e40af', label: 'Embedding' },
    ready:       hasEmbeddings === false
                   ? { bg: '#fef3c7', fg: '#92400e', label: 'Ready (keyword only)' }
                   : { bg: '#dcfce7', fg: '#166534', label: 'Ready' },
    failed:      { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
    deleted:     { bg: '#e5e7eb', fg: '#374151', label: 'Deleted' },
  };
  const c = colors[status] || { bg: '#e5e7eb', fg: '#374151', label: status || 'Unknown' };
  return (
    <span style={{
      marginLeft: 8, fontSize: 11, fontWeight: 600,
      padding: '2px 7px', borderRadius: 999,
      background: c.bg, color: c.fg,
    }}>
      {c.label}
    </span>
  );
}

// ============================================================================
// TestSearchPanel — admin sanity-check the retrieval before AI consumes it
// ============================================================================

function TestSearchPanel({ hasReadyDocs }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [method, setMethod] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function runSearch(e) {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setError(null); setResults(null);
    try {
      const fn = httpsCallable(functions, 'searchDocs');
      const res = await fn({ query: q.trim(), limit: 6 });
      setResults(res.data.chunks || []);
      setMethod(res.data.method || 'unknown');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 24 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost"
        style={{ fontSize: 13 }}>
        {open ? '▾' : '▸'} Test search
      </button>
      {open && (
        <div style={{ marginTop: 12, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
          <p className="settings-help" style={{ marginTop: 0 }}>
            Try a question your customers might ask. See which chunks the AI would
            quote when answering — a useful way to verify your manuals are giving
            good coverage before AI features go live.
          </p>
          {!hasReadyDocs && (
            <div className="placeholder" style={{ marginBottom: 12 }}>
              No indexed documents are ready yet. Upload at least one PDF first.
            </div>
          )}
          <form onSubmit={runSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="search"
              placeholder='e.g. "freezer not cooling overnight"'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={busy || !hasReadyDocs}
              style={{ ...inputStyle, flex: 1, minWidth: 220 }}
            />
            <button
              type="submit"
              disabled={busy || !q.trim() || !hasReadyDocs}
              className="btn-primary">
              {busy ? 'Searching...' : 'Search'}
            </button>
          </form>
          {error && <div className="error-banner" style={{ marginTop: 8 }}>{error}</div>}
          {results !== null && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                {results.length} chunk{results.length === 1 ? '' : 's'}
                {method && <> · matched via <strong>{method}</strong> search</>}
              </div>
              {results.length === 0 && (
                <div className="placeholder">No matching chunks found. Try different wording, or upload a manual covering this topic.</div>
              )}
              {results.map((r, i) => (
                <div key={r.id || i} style={{
                  padding: 10, marginBottom: 6,
                  background: 'white', borderRadius: 6,
                  border: '1px solid var(--border)',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#3730a3', marginBottom: 4 }}>
                    {r.docName}{r.pageNum ? ` · p.${r.pageNum}` : ''}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {r.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function newDocId() {
  // crypto.randomUUID() is widely supported in evergreen browsers; fall back
  // to a timestamp-suffixed random for older WebViews (e.g. ancient Capacitor).
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return 'd_' + crypto.randomUUID().replace(/-/g, '').slice(0, 24);
  }
  return 'd_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function friendlyError(err) {
  // Firebase httpsCallable wraps server HttpsError as { code, message }.
  if (err?.code === 'resource-exhausted') return err.message;
  if (err?.code === 'permission-denied') return 'You don\'t have permission to manage knowledge base documents.';
  if (err?.code === 'failed-precondition') return err.message;
  return err?.message || String(err);
}

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  fontSize: 14,
};
